import { useCallback, useEffect, useMemo, useState } from "react";
import type { Station } from "@/apis/station/station-apis";
import type { Vehicle } from "@/apis/vehicle/vehicle-apis";
import { toVehicleEditData, type VehicleEditData } from "@/domain/vehicle";
import type { PartnerWithCounts } from "@/v2/apis/partners/partners-apis";
import {
  useStations,
  useVehicle,
  useVehicleStatuses,
  useVehicleTypes,
} from "@/v2/queries";
import { usePartners } from "@/v2/queries/partners";
import {
  type BaseEntityModalResult,
  createEntityModalHook,
} from "./createEntityModalHook";
import { useEffectiveWhitelabelId } from "./useEffectiveWhitelabelId";
import { usePartnerAccessEnabled } from "./usePartnerAccessEnabled";

const useBaseBikeModal = createEntityModalHook<Vehicle, number>({
  useQueryHook: useVehicle,
  getEntityId: (vehicle) => vehicle.id,
});

export interface UseBikeModalResult
  extends Omit<BaseEntityModalResult<Vehicle, number>, "entity"> {
  vehicle: VehicleEditData | null;
  statuses: string[];
  bikeTypes: string[];
  stations: Station[];
  linkedStations: Station[];
  partners: PartnerWithCounts[];
  partnerAccessEnabled: boolean;
  setCityId: (cityId: number | null) => void;
  isLoadingDropdowns: boolean;
}

/**
 * Hook for managing bike modal state and data loading.
 *
 * Handles:
 * - Modal visibility state
 * - Loading vehicle data via TanStack Query
 * - Loading related dropdown data (statuses, stations) in parallel
 * - Modal opens as soon as vehicle loads (prevents stale cached data)
 */
export function useBikeModal(): UseBikeModalResult {
  const baseModal = useBaseBikeModal();
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);

  const { data: statuses, isLoading: isLoadingStatuses } = useVehicleStatuses();
  const { data: bikeTypes, isLoading: isLoadingTypes } = useVehicleTypes();

  // Update city when vehicle loads
  useEffect(() => {
    if (baseModal.entity?.city?.id) {
      setSelectedCityId(baseModal.entity.city.id);
    }
  }, [baseModal.entity?.city?.id]);

  // Load all stations for the selected city (only when city is selected)
  const { data: stationsResponse, isLoading: isLoadingStations } = useStations(
    {
      size: 1000,
      cityId: selectedCityId,
    },
    Boolean(selectedCityId)
  );

  // Load linked stations for the selected city (only when city is selected)
  const { data: linkedStationsResponse, isLoading: isLoadingLinked } =
    useStations(
      {
        size: 1000,
        cityId: selectedCityId,
        filter: "linkedStation=true",
      },
      Boolean(selectedCityId)
    );

  const partnerAccessEnabled = usePartnerAccessEnabled();
  const effectiveWhitelabelId = useEffectiveWhitelabelId();
  // Partners are scoped per whitelabel and cached by react-query; the modal
  // never triggers a refetch on open. Skipped entirely when the flag is off.
  const { data: partnersData } = usePartners(
    partnerAccessEnabled ? effectiveWhitelabelId : null
  );

  const isLoadingDropdowns =
    isLoadingStatuses || isLoadingStations || isLoadingLinked || isLoadingTypes;

  const open = useCallback(
    (vehicleId: number) => {
      setSelectedCityId(null);
      baseModal.open(vehicleId);
    },
    [baseModal]
  );

  const close = useCallback(() => {
    setSelectedCityId(null);
    baseModal.close();
  }, [baseModal]);

  const setCityId = useCallback((cityId: number | null) => {
    setSelectedCityId(cityId);
  }, []);

  // Memoize vehicle transformation separately to prevent form resets when stations change
  const vehicle = useMemo(
    () => toVehicleEditData(baseModal.entity),
    [baseModal.entity]
  );

  return useMemo(
    () => ({
      isOpen: baseModal.isOpen,
      vehicle,
      isLoading: baseModal.isLoading,
      statuses: statuses ?? [],
      bikeTypes: bikeTypes ?? [],
      stations: stationsResponse?.content ?? [],
      linkedStations: linkedStationsResponse?.content ?? [],
      partners: partnersData ?? [],
      partnerAccessEnabled,
      open,
      close,
      setCityId,
      isLoadingDropdowns,
    }),
    [
      baseModal.isOpen,
      vehicle,
      baseModal.isLoading,
      statuses,
      bikeTypes,
      stationsResponse?.content,
      linkedStationsResponse?.content,
      partnersData,
      partnerAccessEnabled,
      open,
      close,
      setCityId,
      isLoadingDropdowns,
    ]
  );
}
